import { useEffect, useCallback, useRef } from 'react'
import { useActions } from 'statux'
import { tabbable, isTabbable } from 'tabbable'

import { setSiblingsAriaHidden } from '../../utils/dom'

import useEventListener from '../../hooks/useEventListener'
import useScrollLockToggle, {
    scrollLockToggle
} from '../../hooks/useScrollLockToggle'

import Embed from '../Embed/Embed'
import RichText from '../RichText/RichText'

export default function Modal (props = {
    className: null,
    size: null,
    dialogScrollable: false,
    icon: null,
    iconBackground: null,
    embed: null,
    title: null,
    titlebarCta: null,
    heading: null,
    richText: null,
    component: null,
    refocusRef: null,
    onOpen: null,
    onClose: null
}) {
    const modal = useRef()

    const setModal = useActions('modal')

    const refocusRef = useRef(props.refocusRef || document.activeElement)

    const [scrollLock, toggleScrollLock] = useScrollLockToggle(true)

    useEffect(() => {
        setSiblingsAriaHidden(
            modal.current || { parentElement: document.getElementById('root') },
            scrollLock)

        if (scrollLock
                && modal.current
                && modal.current !== document.activeElement
                && !modal.current.contains(document.activeElement)) {
            const focusEl = modal.current.querySelector('h1[tabindex="-1"]')
                || modal.current.querySelector('h2[tabindex="-1"]')
                || modal.current

            focusEl
                .focus({ preventScroll: true })
        }

        if (scrollLock)
            return typeof props.onOpen === 'function'
                && props.onOpen()

        if (refocusRef.current
                && isTabbable(refocusRef.current))
            refocusRef.current.focus({ preventScroll: true })

        setModal(false)

        if (typeof props.onClose === 'function')
            props.onClose()
    }, [scrollLock])

    useEventListener('keydown', useCallback((e) => {
        if (!scrollLock)
            return

        // On Escape
        if ((e?.key || '').match(/escape/i))
            toggleScrollLock()

        // Trap Tab keypresses
        if (!(e?.key || '').match(/tab/i))
            return

        let activeEl

        const els = tabbable(modal.current) || []

        if (e.shiftKey
                && els.indexOf(e.target) === 0)
            activeEl = els[els.length - 1]

        if (!e.shiftKey
                && els.indexOf(e.target) === els.length - 1)
            activeEl = els[0]

        if (!activeEl)
            return

        e.preventDefault()
        e.stopPropagation()

        activeEl.focus()
    }, [scrollLock]))

    // Catch-all to close modal on unmount
    useEffect(() => () => {
        if (!document.documentElement.classList.contains('scroll-lock'))
            return

        setSiblingsAriaHidden({
            parentElement: document.getElementById('root')
        }, false)

        scrollLockToggle(false)
    }, [])

    // No content to render, bail
    if (!props.embed
            && !(props.heading
                || !props.richText)) {
        toggleScrollLock()
        return null
    }

    let className = `modal ${props.className || ''}`.trim()

    if (props.size)
        className += ` modal-${props.size}`

    if (props.dialogScrollable)
        className += ' modal-dialog-scrollable'

    return (
        <div
            ref={modal}
            id="modal"
            className={className}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby={props.heading
                ? 'modal-heading'
                : props['aria-labelledby'] || null}
            aria-describedby={props.title
                && 'modal-title'}
            onClick={(e) => {
                const dialog = modal.current
                    && modal.current.querySelector('.modal-dialog')

                return dialog
                    && e.target !== dialog
                    && !dialog.contains(e.target)
                    && scrollLock
                    && toggleScrollLock()
            }}
        >
            <div className="container">
                <div className="row flex-justify-content-center">
                    <div
                        className={`col-12 col-10-md ${props.embed
                                || props.size === 'lg'
                            ? 'col-8-lg-up'
                            : 'col-8-lg col-6-xl-up'}`}
                    >
                        <div
                            className={`modal-dialog${!props.embed
                                ? ' card pad-0'
                                : ''}`}
                        >
                            <div
                                className={`modal-titlebar${
                                    props.embed
                                        ? ' margin-b pad-0'
                                        : ''}${
                                    (!props.title || props.embed)
                                        ? ' modal-titlebar-titleless'
                                        : ''}${
                                    props.titlebarCta
                                        ? '  modal-titlebar-cta'
                                        : ''}`}
                            >
                                <div className="row flex-justify-content-space-between flex-align-items-center">
                                    {props.titlebarCta
                                        && (
                                            <div className="col-auto display-inline-flex">
                                                {props.titlebarCta}
                                            </div>)}
                                    {(props.title
                                            || props.embed)
                                        && (
                                            <div className="col-auto margin-auto margin-0-t margin-0-b">
                                                <h1
                                                    id="modal-title"
                                                    className={`body-3 font-weight-book${
                                                        props.embed
                                                            ? ' text-screen-reader'
                                                            : ''}`}
                                                    tabIndex="-1"
                                                >
                                                    {props.title || (props.embed
                                                        ? 'Media player'
                                                        : '')}
                                                </h1>
                                            </div>)}
                                    <div
                                        className="col-auto display-inline-flex"
                                        style={{
                                            marginLeft: 'auto'
                                        }}
                                    >
                                        <button
                                            className="btn-unstyled modal-close-btn"
                                            aria-label="Close disalog"
                                            onClick={() => scrollLock
                                                && toggleScrollLock()}
                                        >
                                            <svg
                                                className="icon-close"
                                                width="18"
                                                height="18"
                                            >
                                                <use xlinkHref="#icon-close" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <Embed
                                className="modal-embed"
                                oembed={typeof props.embed === 'string'
                                    ? props.embed
                                    : null}
                                {...props.embed}
                            />
                            {!props.embed
                                && (
                                    <div className="modal-content text-align-center">
                                        {props.icon
                                            && (
                                                <div className="display-flex flex-justify-content-center margin-b">
                                                    <div
                                                        className={`modal-icon${
                                                            props.iconBackground
                                                                ? ' avatar'
                                                                : ''}`}
                                                        style={{
                                                            backgroundColor: props.iconBackground
                                                        }}
                                                    >
                                                        <svg className={props.icon}>
                                                            <use xlinkHref={`#${props.icon}`} />
                                                        </svg>
                                                    </div>
                                                </div>)}
                                        {props.heading
                                            && (
                                                <h2
                                                    id="modal-heading"
                                                    className={(props.richText
                                                            || props.component)
                                                        && 'text-underscore-condensed-center'}
                                                    tabIndex="-1"
                                                >
                                                    {props.heading}
                                                </h2>)}
                                        <RichText
                                            html={props.richText}
                                        />
                                        {props.component
                                            && props.component}
                                    </div>)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
